(ns ballot-scout-v2-frontend.routes
  (:require
   [ballot-scout-v2-frontend.auth :as auth]
   [ballot-scout-v2-frontend.dashboard.events :as e.dashboard]
   [ballot-scout-v2-frontend.dashboard.views :as v.dashboard]
   [ballot-scout-v2-frontend.disposition-manager.events :as e.disposition]
   [ballot-scout-v2-frontend.disposition-manager.views :as v.disposition]
   [ballot-scout-v2-frontend.email.events :as e.email]
   [ballot-scout-v2-frontend.email.views :as v.email]
   [ballot-scout-v2-frontend.embed-mockup.views :as v.embed-mockup]
   [ballot-scout-v2-frontend.events :as events]
   [ballot-scout-v2-frontend.global-stats.events :as e.global-stats]
   [ballot-scout-v2-frontend.global-stats.views :as v.global-stats]
   [ballot-scout-v2-frontend.import-manager.events :as e.import]
   [ballot-scout-v2-frontend.import-manager.views :as v.import]
   [ballot-scout-v2-frontend.resources.events :as e.resources]
   [ballot-scout-v2-frontend.resources.views :as v.resources]
   [ballot-scout-v2-frontend.views :as views]
   [ballot-scout-v2-frontend.voter-lookup.events :as e.voter-lookup]
   [ballot-scout-v2-frontend.voter-lookup.views :as v.voter-lookup]
   [ballot-scout-v2-frontend.widget.events :as e.widget]
   [ballot-scout-v2-frontend.widget.views :as v.widget]
   [re-frame.core :as rf]
   [reitit.coercion.spec :as rtss]
   [reitit.frontend :as rtf]
   [reitit.frontend.easy :as rtfe])
  (:require-macros
   [ballot-scout-v2-frontend.config-helpers :refer [required-env-var]])
  (:import
   (goog.html TrustedResourceUrl)
   (goog.string Const)))

(def google-maps-url
  (let [google-maps-key (required-env-var :google-maps-api-key)]
    (TrustedResourceUrl.fromConstant
     (Const.from
      (str "https://maps.googleapis.com/maps/api/js?v=weekly&key="
           google-maps-key)))))

;;; Effects ;;;

;; Triggering navigation from events.
(rf/reg-fx
 ::navigate!
 (fn [route]
   (apply rtfe/push-state route)))

;; ::redirect! is like ::navigate! but doesn't create a new history entry
(rf/reg-fx
 ::redirect!
 (fn [route]
   (apply rtfe/replace-state route)))

;;; Events ;;;

(rf/reg-event-fx
 ::navigate
 (fn [_ [_ & route]]
   {::navigate! route}))

(rf/reg-event-fx
 ::redirect
 (fn [_ [_ & route]]
   {::redirect! route}))

(defn- make-fx-dispatch-vec
  [event-or-event-vec to-append]
  [:dispatch
   (if (vector? event-or-event-vec)
     event-or-event-vec
     [event-or-event-vec to-append])])

(defn- get-current-route [db]
  (:current-route db))

(rf/reg-event-fx
 ::navigated
 (fn [{:keys [db]} [_ match]]
   (let [{:keys [dispatch dispatch-n page-name]} (:data match)
         effects [[:dispatch [::events/scroll-to-top]]
                  [:dispatch [::events/clear-page-name]]
                  [:dispatch [::events/set-view-mobile-menu false]]]
         effects (if-not match
                   (conj effects [:dispatch [::events/trigger-404]])
                   (concat effects
                           [[:dispatch [::events/set-page-name page-name]]]
                           (when dispatch [(make-fx-dispatch-vec dispatch match)])
                           (mapv (fn [e] (make-fx-dispatch-vec e match)) dispatch-n)))]
     (merge {:fx effects}
            (when match
              {:db (assoc db :current-route match)})))))

;;; Subscriptions ;;;

(rf/reg-sub
 ::current-route
 (fn [db]
   (get-current-route db)))

;; Routes

;; Each route entry needs to have a :name
;; The following keys are optional
;; :view - The page component that should be rendered when that route is dispatched
;; :dispatch - Dispatches the given event with the route as the argument
;; :dispatch-n - Directly mirrors the dispatch-n effect. It's events are *not* given the route as an argument

(def routes
  [["/"
    {:name ::index
     :view views/front-page
     :dispatch ::events/prep-index-page
     :public? true}]

   ["/login"
    {:name ::login
     :dispatch ::auth/login
     :page-name "Login"
     :public? true}]

   ["/login-callback"
    {:name ::login-callback
     :dispatch ::auth/login-callback
     :public? true}]

   ["/logout"
    {:name ::logout
     :dispatch ::auth/logout
     :page-name "Logout"
     :public? true}]

   ["/login-failure"
    {:name ::login-failure
     :view views/login-failure-page
     :page-name "Login Failure"
     :public? true}]

   ["/deprecated-widget"
    {:name ::deprecated-widget
     :view v.widget/deprecation-message
     :page-name "Outdated Ballot Scout Version"
     :public? true}]

;; The following two routes are to support redirects for 2 south carolina widgets
   ;; These should be removed once the jurisdictions update their widget embeds.
   ;; See BS-566 for more information
   ["/charleston-county/search"
    {:name ::charleston-county-widget-redirect
     :dispatch [::redirect
                ::jurisdiction-widget
                {:jurisdiction-slug "CHARLESTON"}]
     :public? true}]

   ["/dorchester-county/search"
    {:name ::dorchester-county-widget-redirect
     :dispatch [::redirect
                ::jurisdiction-widget
                {:jurisdiction-slug "DORCHESTER"}]
     :public? true}]

   ["/:jurisdiction-slug/search"
    {:name ::jurisdiction-widget
     :parameters {:path {:jurisdiction-slug string?}}
     :view v.widget/page
     :dispatch ::e.widget/load-jurisdiction-from-route-for-widget
     :page-name "Widget"
     :container? false
     :public? true}]

   ["/:partner-slug/search/state"
    {:name ::partner-widget
     :parameters {:path {:partner-slug string?}}
     :view v.widget/partner-page
     :dispatch ::e.widget/load-partner-from-route-for-widget
     :page-name "State Widget"
     :container? false
     :public? true}]

   ["/:jurisdiction-slug/track"
    {:name ::jurisdiction-widget-title
     :parameters {:path {:jurisdiction-slug string?}}
     :view v.widget/title-page
     :dispatch ::e.widget/load-jurisdiction-from-route-for-widget
     :page-name "Widget"
     :container? false
     :public? true}]

   ["/:partner-slug/track/state"
    {:name ::partner-widget-title
     :parameters {:path {:partner-slug string?}}
     :view v.widget/title-partner-page
     :dispatch ::e.widget/load-partner-from-route-for-widget
     :page-name "State Widget"
     :container? false
     :public? true}]

   ["/:jurisdiction-slug/search/admin"
    {:name ::jurisdiction-widget-admin
     :parameters {:path {:jurisdiction-slug string?}}
     :view v.widget/admin-page
     :dispatch-n [::e.widget/load-jurisdiction-from-route-for-widget
                  ::e.widget/set-demo-keystrokes]
     :page-name "Widget"
     :container? false
     :public? false}]

   ["/:partner-slug/search/state/admin"
    {:name ::partner-widget-admin
     :parameters {:path {:partner-slug string?}}
     :view v.widget/admin-partner-page
     :dispatch-n [::e.widget/load-partner-from-route-for-widget
                  ::e.widget/set-demo-keystrokes]
     :page-name "State Widget"
     :container? false
     :public? false}]

   ["/:jurisdiction-slug/embed-mockup"
    {:name ::jurisdiction-widget-embed-mockup
     :parameters {:path {:jurisdiction-slug string?}}
     :view v.embed-mockup/page
     :dispatch ::events/load-jurisdiction-route
     :page-name "Embed Mockup"
     :container? false
     :public? false}]

   ["/:partner-slug/:election-slug/home"
    {:name ::partner-home
     :parameters {:path {:partner-slug string?
                         :election-slug string?}}
     :view v.dashboard/partner-page
     :dispatch-n [::events/load-partner-route
                  ::e.dashboard/fetch-ballots-by-location-and-partner
                  ::e.dashboard/fetch-days-in-service-for-partner
                  ::e.dashboard/fetch-ballots-by-status-and-partner
                  ::e.dashboard/fetch-noty-stats-for-partner
                  ::e.dashboard/clear-dis-export-state
                  [::e.dashboard/load-google-maps-api google-maps-url]]
     :page-name "Partner Home"
     :partner-admin-page? true
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/home"
    {:name ::jurisdiction-home
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?}}
     :view v.dashboard/jurisdiction-page
     :dispatch-n [::events/load-jurisdiction-route
                  ::e.dashboard/fetch-ballots-by-location-and-jurisdiction
                  ::e.dashboard/fetch-days-in-service-for-jurisdiction
                  ::e.dashboard/fetch-ballots-by-status-and-jurisdiction
                  ::e.dashboard/fetch-noty-stats-for-jurisdiction
                  ::e.dashboard/clear-dis-export-state
                  [::e.dashboard/load-google-maps-api google-maps-url]]
     :page-name "Jurisdiction Home"
     :container? true}]

   ["/:partner-slug/:election-slug/voter-lookup"
    {:name ::partner-voter-lookup
     :parameters {:path {:partner-slug string?
                         :election-slug string?}}
     :view v.voter-lookup/partner-page
     :dispatch-n [::events/load-partner-route
                  ::e.voter-lookup/partner-voter-lookup-navigate
                  ::e.voter-lookup/clear-export-state]
     :page-name "Partner Voter Lookup"
     :partner-admin-page? true
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/voter-lookup"
    {:name ::jurisdiction-voter-lookup
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?}}
     :view v.voter-lookup/jurisdiction-page
     :dispatch-n [::events/load-jurisdiction-route
                  ::e.voter-lookup/jurisdiction-voter-lookup-navigate
                  ::e.voter-lookup/clear-export-state]
     :page-name "Jurisdiction Voter Lookup"
     :container? true}]

   ["/verify-email"
    {:name ::verify-email
     :view v.email/verify-email-page
     :dispatch-n [::e.email/verify-email]
     :page-name "Verify Email"
     :public? true}]

   ["/unsubscribe-email"
    {:name ::unsubscribe-email
     :view v.email/unsubscribe-email-page
     :dispatch-n [::e.email/unsubscribe-email]
     :page-name "Unsubscribe Email"
     :public? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/resources"
    {:name ::resources
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?}}
     :view v.resources/page
     :dispatch-n [::events/load-jurisdiction-route
                  ::e.resources/fetch-resource-urls]
     :page-name "Resources"
     :container? true}]

   ["/:partner-slug/resources"
    {:name ::partner-resources
     :parameters {:path {:partner-slug string?}}
     :view v.resources/partner-page
     :dispatch-n [::events/load-partner-route
                  ::e.resources/fetch-resource-urls]
     :page-name "Resources"
     :partner-admin-page? true
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/imports"
    {:name ::import-list
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?}}
     :view v.import/list-page
     :dispatch-n [::events/load-jurisdiction-route
                  ::e.import/clear-import-state
                  ::e.import/fetch-imports-list-for-route]
     :page-name "Imports"
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/imports/new"
    {:name ::create-import
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?}}
     :view v.import/create-page
     :dispatch-n [:ballot-scout-v2-frontend.import-manager.events/clear
                  ::events/load-jurisdiction-route
                  ::e.resources/fetch-resource-urls]
     :page-name "Create Import"
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/imports/:import-id"
    {:name ::import-manager
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?
                         :import-id integer?}}
     :view v.import/manager-page
     :dispatch-n [::events/load-jurisdiction-route
                  ::events/load-import-for-route]
     :page-name "View Import"
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/dispositions"
    {:name ::disposition-list-for-jurisdiction
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?}}
     :view v.disposition/jurisdiction-list-page
     :dispatch-n [::events/load-jurisdiction-route
                  ::e.disposition/clear-disposition-state
                  ::e.disposition/fetch-dispositions-list-for-jurisdiction-route]
     :page-name "Jurisdiction Dispositions"
     :container? true}]

   ["/:partner-slug/:election-slug/dispositions"
    {:name ::disposition-list-for-partner
     :parameters {:path {:partner-slug string?
                         :election-slug string?}}
     :view v.disposition/partner-list-page
     :dispatch-n [::events/load-partner-route
                  ::e.disposition/clear-disposition-state
                  ::e.disposition/fetch-dispositions-list-for-partner-route]
     :page-name "Partner Dispositions"
     :partner-admin-page? true
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/dispositions/new"
    {:name ::create-disposition-for-jurisdiction
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?}}
     :view v.disposition/jurisdiction-create-page
     :dispatch-n [:ballot-scout-v2-frontend.disposition-manager.events/clear
                  ::events/load-jurisdiction-route
                  ::e.resources/fetch-resource-urls]
     :page-name "Jurisdiction Create Disposition"
     :container? true}]

   ["/:partner-slug/:election-slug/dispositions/new"
    {:name ::create-disposition-for-partner
     :parameters {:path {:partner-slug string?
                         :election-slug string?}}
     :view v.disposition/partner-create-page
     :dispatch-n [:ballot-scout-v2-frontend.disposition-manager.events/clear
                  ::events/load-partner-route
                  ::e.resources/fetch-resource-urls]
     :page-name "Partner Create Disposition"
     :partner-admin-page? true
     :container? true}]

   ["/:partner-slug/:jurisdiction-slug/:election-slug/dispositions/:disposition-id"
    {:name ::jurisdiction-disposition-manager
     :parameters {:path {:partner-slug string?
                         :jurisdiction-slug string?
                         :election-slug string?
                         :disposition-id integer?}}
     :view v.disposition/manager-page
     :dispatch-n [::events/load-jurisdiction-route
                  ::events/load-disposition-for-route]
     :page-name "Jurisdiction View Disposition"
     :container? true}]

   ["/:partner-slug/:election-slug/dispositions/:disposition-id"
    {:name ::partner-disposition-manager
     :parameters {:path {:partner-slug string?
                         :election-slug string?
                         :disposition-id integer?}}
     :view v.disposition/manager-page
     :dispatch-n [::events/load-partner-route
                  ::events/load-disposition-for-route]
     :page-name "Partner View Disposition"
     :partner-admin-page? true
     :container? true}]

   ["/:partner-slug/statistics"
    {:name ::global-stats
     :view v.global-stats/page
     :dispatch-n [::e.global-stats/global-stats-navigate]
     :page-name "Global Stats"
     :container? true}]])

(def router
  (rtf/router routes {:data {:coercion rtss/coercion}
                      :conflicts nil}))

(defn match-by-path [path]
  (rtf/match-by-path router path))

(defn public-path? [path]
  (-> path match-by-path :data :public?))

(rf/reg-cofx
 ::on-public-page?
 (fn [cofx]
   (assoc cofx :on-public-page? (public-path? (.. js/window -location)))))

(rf/reg-event-fx
 ::path-navigate
 (fn [_ [_ path]]
   (let [match (rtf/match-by-path router path)]
     {::navigate! [(get-in match [:data :name])
                   (:path-params match)
                   (:query-params match)]})))

(defn app-routes []
  (rtfe/start! router
               (fn [match]
                 (rf/dispatch [::navigated match]))
               {:use-fragment false}))
